(function () {
    'use strict';

    if (typeof window === 'undefined') {
        const _ = require('lodash');
        module.exports = exports(_);

    } else {
        RivalIQ.apps.AdsInsights.data.ObservationsGenerator = exports(window._);
    }

    // minumum number of results necessary to generate an observation
    const MIN_NUM_RESULTS = 10;

    // minimum number of results a row must have to generate an observation.
    const MIN_NUM_ROW_RESULTS = 5;

    // minumum spend percentage per row.
    const MIN_SPEND_PCT = 0.02;

    // generate an observation if the row is at least 30% higher/lower than its parent.
    const SIGNIFICANT_DEVIATION_THRESHOLD = 0.3;


    function exports(_) {
        function createParentMetric(baseMetric, overrides) {
            return RivalIQ.metric.build(baseMetric, _.extend({
                breakdownName: undefined,
                childObjectType: undefined,
                dimensions: ['channel', 'adObjectId', 'timeAnalysisMode']
            }, overrides), {
                clobberDimensions: true
            });
        }

        function createRowMetrics(baseMetric, rowMetrics) {
            return RivalIQ.metric.build(baseMetric, _.map(rowMetrics, function (rowMetric) {
                const metric = (_.isObject(rowMetric) ? rowMetric : {
                    property: rowMetric
                });
                return _.defaults({
                    limit: -1
                }, metric);
            }));
        }

        // convenience function as many observeration configs want some metrics on each row
        // and for the parent, and each row's adObject metric for name and whatnot.
        function createObservationMetrics(baseMetric, properties) {
            const parentMetrics = _.map(properties, function (property) {
                return createParentMetric(baseMetric, {
                    property: property
                });
            });

            const rowMetrics = createRowMetrics(baseMetric, properties.concat(['adObject']));
            return parentMetrics.concat(rowMetrics);
        }

        function generateObservationDisplayData(idx,
            adObjectsMetric,
            primaryMetric,
            parentAverageMetric) {

            const adObject = _.last(adObjectsMetric.getValue()[idx]);

            const primaryRowValue = _.last(primaryMetric.getValue({
                rawValue: true
            })[idx]);
            const parentAverageValue = parentAverageMetric.getValue({
                rawValue: true
            });

            const compareWord = (_.last(primaryMetric.getValue({
                rawValue: true
            })[idx]) > parentAverageMetric.getValue({
                rawValue: true
            }) ?
                'higher' : 'lower');

            return {
                subjectName: adObject.name,
                compareWord: compareWord,
                metricName: primaryMetric.getName(),
                percentDifference: RivalIQ.formatter.absolutePercent(Math.abs(1 - primaryRowValue / parentAverageValue), {
                    decimals: 0
                }),
                averageValue: parentAverageMetric.getValue()
            };
        }

        function rowMeetsBaseRequirements(parentResultsValue, rowResultCount, parentSpendValue, rowSpendValue) {
            return (
                parentResultsValue >= MIN_NUM_RESULTS &&
                rowResultCount >= MIN_NUM_ROW_RESULTS &&
                parentSpendValue * MIN_SPEND_PCT <= rowSpendValue
            );
        }

        // the set of configs that we will generate observations against.
        const ObservationConfigs = [{
            // creates an observation if a value is N times higher than expected cost_per_result
            isApplicableToMetricList: _.constant(true), // show these observations regardless of what metrics are in the user's table
            getRequiredMetrics: function (baseMetric) {
                return createObservationMetrics(RivalIQ.metric.build(baseMetric, {
                    orderBy: 'cost_per_result',
                    sortOrder: 1 // descending
                }), ['cost_per_result', 'results', 'spend']);
            },
            generateObservations: function (results, maxObservations) {
                const parentCostPerResultMetric = results.at(0);
                const parentResultsMetric = results.at(1);
                const parentSpendMetric = results.at(2);
                const childrenCostPerResultMetric = results.at(3);
                const childrenResultsMetric = results.at(4);
                const childrenSpendMetric = results.at(5);
                const childrenAdObjectsMetric = results.at(6);

                const parentResultsValue = parentResultsMetric.getValue({
                    rawValue: true
                });
                const parentSpendValue = parentSpendMetric.getValue({
                    rawValue: true
                });
                const parentCostPerResultValue = parentCostPerResultMetric.getValue({
                    rawValue: true
                });
                const costPerResultValues = childrenCostPerResultMetric.getValue({
                    rawValue: true
                });
                const resultCounts = childrenResultsMetric.getValue({
                    rawValue: true
                });
                const spendValues = childrenSpendMetric.getValue({
                    rawValue: true
                });

                return _(costPerResultValues)
                    .map(function (row, idx) {
                        const rowCostPerResult = _.last(row);
                        const rowResultCount = _.last(resultCounts[idx]);
                        const rowSpendValue = _.last(spendValues[idx]);

                        if (rowCostPerResult > parentCostPerResultValue * (1 + SIGNIFICANT_DEVIATION_THRESHOLD) &&
                            rowMeetsBaseRequirements(parentResultsValue, rowResultCount, parentSpendValue, rowSpendValue)) {

                            const observationDisplayData = generateObservationDisplayData(idx,
                                childrenAdObjectsMetric,
                                childrenCostPerResultMetric,
                                parentCostPerResultMetric);

                            return _.extend({
                                name: RivalIQ.strings.facebook_ads_insights_high_cost_per_result_observation_name,
                                _score: -rowCostPerResult / parentCostPerResultValue,
                                rowId: _.head(row),
                                observationClass: 'observation-negative',
                                description: RivalIQ.strings.facebook_ads_insights_high_cost_per_result_observation_description
                            }, observationDisplayData);
                        }
                    })
                    .compact()
                    .take(maxObservations)
                    .value();
            }
        }, {
            // creates an observation if a value is N times lower than expected cost_per_result
            isApplicableToMetricList: _.constant(true),
            getRequiredMetrics: function (baseMetric) {
                return createObservationMetrics(RivalIQ.metric.build(baseMetric, {
                    orderBy: 'cost_per_result',
                    sortOrder: -1 // ascending
                }), ['cost_per_result', 'results', 'spend']);
            },
            generateObservations: function (results, maxObservations) {
                const parentCostPerResultMetric = results.at(0);
                const parentResultsMetric = results.at(1);
                const parentSpendMetric = results.at(2);
                const childrenCostPerResultMetric = results.at(3);
                const childrenResultsMetric = results.at(4);
                const childrenSpendMetric = results.at(5);
                const childrenAdObjectsMetric = results.at(6);

                const parentResultsValue = parentResultsMetric.getValue({
                    rawValue: true
                });
                const parentSpendValue = parentSpendMetric.getValue({
                    rawValue: true
                });
                const parentCostPerResultValue = parentCostPerResultMetric.getValue({
                    rawValue: true
                });
                const costPerResultValues = childrenCostPerResultMetric.getValue({
                    rawValue: true
                });
                const resultCounts = childrenResultsMetric.getValue({
                    rawValue: true
                });
                const spendValues = childrenSpendMetric.getValue({
                    rawValue: true
                });

                return _(costPerResultValues)
                    .map(function (row, idx) {
                        const rowCostPerResult = _.last(row);
                        const rowResultCount = _.last(resultCounts[idx]);
                        const rowSpendValue = _.last(spendValues[idx]);

                        if (rowCostPerResult < parentCostPerResultValue * (1 - SIGNIFICANT_DEVIATION_THRESHOLD) &&
                            rowMeetsBaseRequirements(parentResultsValue, rowResultCount, parentSpendValue, rowSpendValue)) {
                            const observationDisplayData = generateObservationDisplayData(idx,
                                childrenAdObjectsMetric,
                                childrenCostPerResultMetric,
                                parentCostPerResultMetric);

                            return _.extend({
                                name: RivalIQ.strings.facebook_ads_insights_low_cost_per_result_observation_name,
                                _score: rowCostPerResult / parentCostPerResultValue,
                                rowId: _.head(row),
                                observationClass: 'observation-positive',
                                description: RivalIQ.strings.facebook_ads_insights_low_cost_per_result_observation_description
                            }, observationDisplayData);
                        }
                    })
                    .compact()
                    .take(maxObservations)
                    .value();
            }
        }];

        function _generateTopObservations(observationConfigs, resultSets, maxObservations) {
            let observationsByConfigIdx = _(observationConfigs)
                .map(function (observationConfig, idx) {
                    return _.sortBy(observationConfig.generateObservations(resultSets[idx], maxObservations), '_score');
                })
                .reject(_.isEmpty)
                .value();

            // round robin so that all observation configs are equally represented
            let observations = [];
            while (observations.length < maxObservations && !_.isEmpty(observationsByConfigIdx)) {
                // remove the top observation for each observationConfig.
                let topRemainingObservations = _.reduce(observationsByConfigIdx, function (memo, observations) {
                    memo.push(observations.shift());
                    return memo;
                }, []);
                observationsByConfigIdx = _.reject(observationsByConfigIdx, _.isEmpty);
                topRemainingObservations = _.sortBy(topRemainingObservations, '_score');

                const slotsLeft = maxObservations - observations.length;
                observations = observations.concat(_.take(topRemainingObservations, slotsLeft));
            }

            return observations;
        }

        function createGenerator() {

            return {
                // returns a promise with a set of observations
                // baseMetric should be the metric we're generating against (e.g., the metric for the ads table).
                // metrics should be the set of metrics we're analyzing. An observation is valid iff it's primarily
                // against one of the metrics in this list (e.g., if there's no "spend" metric in the list, don't
                // show an obseration against spend!).
                generateObservations: function (baseMetric, metrics, maxObservations) {

                    const observationConfigs = _.filter(ObservationConfigs, function (observationConfig) {
                        return observationConfig.isApplicableToMetricList(metrics);
                    });
                    let metricFetches = _.map(observationConfigs, function (observationConfig) {
                        const requiredMetrics = observationConfig.getRequiredMetrics(baseMetric);
                        return new RivalIQ.collections.MetricDefinitions(requiredMetrics).fetch();
                    });

                    const objectivesMetric = new RivalIQ.models.MetricDefinition(createParentMetric(baseMetric, {
                        property: 'objective'
                    }));
                    metricFetches = [objectivesMetric.fetch()].concat(metricFetches);

                    return $.when.apply(undefined, metricFetches).then(function () {
                        const resultSets = [].slice.call(arguments);
                        const objective = resultSets.shift().getValue();

                        if (objective === 'MULTIPLE') {
                            // It doesn't make sense generate to observations for mixed objectives since the comparison of results
                            // or cost per result would be meaningless for results of different types.
                            return [];
                        } else {
                            return _generateTopObservations(observationConfigs, resultSets, maxObservations);
                        }
                    });
                }
            };
        }

        return {
            createGenerator: createGenerator,
            _generateTopObservations: _generateTopObservations,
            _ObservationConfigs: ObservationConfigs
        };
    }
})();
